import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button, Card, CardHeader, CardTitle, CardContent, Checkbox, TimeSeriesChart } from '@agion/ui'
import { Globe, Search } from 'lucide-react'
import { cn } from '@/core/analytics_utils'
import { queryInstant, queryRange } from '@/core/observability_api_client'
import type { RouteMetrics, TimeSeriesPoint, PromQLResult } from '@/core/observability_types'

function hasRouteSignal(route: RouteMetrics): boolean {
  return route.requestRate > 0 || route.errorRate > 0 || route.latencyP95 > 0
}

function routeKey(metric: Record<string, string>): string {
  return [
    metric['route'] || '',
    metric['matched_host'] || '',
    metric['matched_uri'] || '',
    metric['service'] || '',
  ].join('\u0000')
}

function inferOwnerService(route: string, matchedHost: string, matchedUri: string): string | undefined {
  const haystack = `${route} ${matchedHost} ${matchedUri}`.toLowerCase()
  if (haystack.includes('analytics') || matchedUri.startsWith('/v1/observability')) return 'analytics-service'
  if (haystack.includes('auth') || matchedUri.startsWith('/auth') || matchedUri.startsWith('/v1/auth')) return 'auth-service'
  if (haystack.includes('streaming') || matchedUri.startsWith('/v1/streaming')) return 'streaming-service'
  if (
    haystack.includes('orchestration') ||
    haystack.includes('missions') ||
    matchedUri.startsWith('/v1/workflows') ||
    matchedUri.startsWith('/v1/executions') ||
    matchedUri.startsWith('/v1/hooks')
  ) return 'orchestration-service'
  if (haystack.includes('infra') || matchedUri.startsWith('/v1/infra')) return 'infra-service'
  if (
    haystack.includes('governance') ||
    haystack.includes('agent-hosts') ||
    matchedUri.startsWith('/v1/host') ||
    matchedUri.startsWith('/v1/providers') ||
    matchedUri.startsWith('/v1/integrations') ||
    matchedUri.startsWith('/v1/models')
  ) return 'governance-service'
  return undefined
}

export const GatewayPage: React.FC = () => {
  const { t } = useTranslation('pages')
  const navigate = useNavigate()
  const [routes, setRoutes] = useState<RouteMetrics[]>([])
  const [showInactiveRoutes, setShowInactiveRoutes] = useState(false)
  const [totalRate, setTotalRate] = useState(0)
  const [totalErrorRate, setTotalErrorRate] = useState(0)
  const [rateTimeline, setRateTimeline] = useState<TimeSeriesPoint[]>([])
  const [gatewayWarning, setGatewayWarning] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchGateway = useCallback(async () => {
    const warnings: string[] = []
    const readInstant = async (query: string, label: string): Promise<PromQLResult[]> => {
      try {
        const res = await queryInstant(query)
        if (res.status === 'error') {
          warnings.push(label)
          return []
        }
        return (res.data?.result || []) as PromQLResult[]
      } catch {
        warnings.push(label)
        return []
      }
    }

    const readRange = async (query: string, start: string, end: string, step: string) => {
      try {
        const res = await queryRange(query, start, end, step)
        if (res.status === 'error') {
          warnings.push(t('observability.requestRateTimeline', 'Request Rate (Last Hour)'))
          return []
        }
        return res.data?.result?.[0]?.values || []
      } catch {
        warnings.push(t('observability.requestRateTimeline', 'Request Rate (Last Hour)'))
        return []
      }
    }

    try {
      const [rateResults, errorResults, p95Results] = await Promise.all([
        readInstant('sum(rate(apisix_http_status[5m])) by (route, matched_host, matched_uri, service)', t('observability.rate', 'Rate')),
        readInstant('sum(rate(apisix_http_status{code=~"5.."}[5m])) by (route, matched_host, matched_uri, service) / sum(rate(apisix_http_status[5m])) by (route, matched_host, matched_uri, service)', t('observability.errors', 'Errors')),
        readInstant('histogram_quantile(0.95, sum(rate(apisix_http_latency_bucket[5m])) by (le, route, matched_host, matched_uri, service))', 'P95'),
      ])

      const rateMap = new Map<string, number>()
      const errorMap = new Map<string, number>()
      const p95Map = new Map<string, number>()
      const routeMeta = new Map<string, Pick<RouteMetrics, 'route' | 'matchedHost' | 'matchedUri' | 'gatewayService' | 'ownerService'>>()

      const rememberRoute = (r: PromQLResult) => {
        const route = r.metric['route'] || ''
        if (!route) return ''
        const key = routeKey(r.metric)
        if (!routeMeta.has(key)) {
          const matchedHost = r.metric['matched_host'] || ''
          const matchedUri = r.metric['matched_uri'] || ''
          routeMeta.set(key, {
            route,
            matchedHost,
            matchedUri,
            gatewayService: r.metric['service'] || '',
            ownerService: inferOwnerService(route, matchedHost, matchedUri),
          })
        }
        return key
      }

      for (const r of rateResults) {
        const key = rememberRoute(r)
        if (key && r.value) {
          rateMap.set(key, parseFloat(r.value[1]) || 0)
        }
      }
      for (const r of errorResults) {
        const key = rememberRoute(r)
        if (key && r.value) {
          errorMap.set(key, parseFloat(r.value[1]) || 0)
        }
      }
      for (const r of p95Results) {
        const key = rememberRoute(r)
        if (key && r.value) {
          p95Map.set(key, parseFloat(r.value[1]) || 0)
        }
      }

      const routeList: RouteMetrics[] = []
      for (const [key, meta] of routeMeta.entries()) {
        const rate = rateMap.get(key) || 0
        const err = errorMap.get(key) || 0
        routeList.push({
          ...meta,
          requestRate: rate,
          errorRate: isNaN(err) ? 0 : err,
          latencyP95: p95Map.get(key) || 0,
        })
      }
      routeList.sort((a, b) => b.requestRate - a.requestRate)

      setRoutes(routeList)
      setTotalRate(routeList.reduce((sum, r) => sum + r.requestRate, 0))
      const totalReqs = routeList.reduce((sum, r) => sum + r.requestRate, 0)
      const totalErrs = routeList.reduce((sum, r) => sum + r.requestRate * r.errorRate, 0)
      setTotalErrorRate(totalReqs > 0 ? totalErrs / totalReqs : 0)

      // Timeline
      const now = Math.floor(Date.now() / 1000)
      const values = await readRange(
        'sum(rate(apisix_http_status[5m]))',
        String(now - 3600), String(now), '60'
      )
      setRateTimeline(values.map(([ts, val]: [number, string]) => ({
        time: ts * 1000,
        value: parseFloat(val) || 0,
      })))
      setGatewayWarning(warnings.length > 0
        ? `${t('observability.gatewayPartialData', 'Some gateway metrics could not be loaded:')} ${warnings.join(', ')}`
        : null)
    } catch {
      setGatewayWarning(t('observability.gatewayLoadFailed', 'Gateway metrics could not be loaded. Previous data is still shown if available.'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchGateway()
    const interval = setInterval(fetchGateway, 30000)
    return () => clearInterval(interval)
  }, [fetchGateway])

  const timelineSeries = rateTimeline.length > 0 ? [{
    label: t('observability.requestRate', 'Request Rate'),
    color: '#3b82f6',
    values: rateTimeline.map(p => ({ date: new Date(p.time).toISOString(), value: p.value })),
  }] : []

  const openRouteLogs = (route: RouteMetrics) => {
    const params = new URLSearchParams()
    if (route.ownerService) params.set('service', route.ownerService)
    params.set('route', route.route)
    if (route.errorRate > 0) params.set('level', 'error')
    navigate(`/infrastructure/logs?${params.toString()}`)
  }

  const activeRoutes = routes.filter(hasRouteSignal)
  const visibleRoutes = showInactiveRoutes ? routes : activeRoutes
  const inactiveRouteCount = routes.length - activeRoutes.length
  const inactiveRoutesHiddenText = t('observability.inactiveRoutesHidden', '{{count}} inactive hidden', { count: inactiveRouteCount })
    .replace('{{count}}', String(inactiveRouteCount))

  return (
    <div className="container-standard">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Globe className="h-6 w-6 text-slate-600 dark:text-slate-400" />
          <div>
            <h1 className="page-title">{t('observability.gatewayTitle', 'Gateway')}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('observability.gatewayDescription', 'APISIX request rates, latency, and errors per route')}
            </p>
          </div>
        </div>
      </div>

      {/* Partial data warning */}
      {gatewayWarning && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200">
          {gatewayWarning}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.totalRoutes', 'Routes')}</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{activeRoutes.length}</p>
            {inactiveRouteCount > 0 && (
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {inactiveRoutesHiddenText}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.totalRequestRate', 'Total Rate')}</p>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <p className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{`${totalRate.toFixed(1)}/s`}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('observability.errorRate', 'Error Rate')}</p>
            <p className={cn('text-2xl font-semibold', totalErrorRate > 0.05 ? 'text-red-600' : 'text-slate-900 dark:text-slate-100')}>
              {(totalErrorRate * 100).toFixed(2)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Request rate timeline */}
      {timelineSeries.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">{t('observability.requestRateTimeline', 'Request Rate (Last Hour)')}</CardTitle>
          </CardHeader>
          <CardContent>
            <TimeSeriesChart
              data={timelineSeries}
              height={200}
              showLegend={false}
              showDots={false}
              formatY={(v: number) => `${v.toFixed(1)}/s`}
              formatX={(d: Date) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            />
          </CardContent>
        </Card>
      )}

      {/* Routes table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-sm">{t('observability.routeMetrics', 'Route Metrics')}</CardTitle>
            {inactiveRouteCount > 0 && (
              <Checkbox
                id="show-inactive-routes"
                className="h-4 w-4"
                checked={showInactiveRoutes}
                onCheckedChange={(checked) => setShowInactiveRoutes(checked === true)}
                label={t('observability.showInactiveRoutes', 'Show inactive routes')}
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {/* eslint-disable-next-line no-restricted-syntax */}
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.route', 'Route')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.rate', 'Rate')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.errors', 'Errors')}</th>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">P95</th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">{t('observability.investigate', 'Investigate')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50">
                      <td colSpan={5} className="px-4 py-3"><div className="h-5 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" /></td>
                    </tr>
                  ))
                ) : visibleRoutes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-slate-400">
                      {t('observability.noRoutes', 'No APISIX metrics found. The gateway may not be exporting metrics yet.')}
                    </td>
                  </tr>
                ) : (
                  visibleRoutes.map(r => (
                    <tr key={`${r.route}:${r.matchedHost}:${r.matchedUri}:${r.gatewayService}`} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/20">
                      <td className="px-4 py-3 text-sm font-medium">
                        <Button
                          type="button"
                          variant="link"
                          className="h-auto max-w-[52rem] justify-start truncate p-0 text-left font-medium text-slate-900 hover:text-blue-600 dark:text-slate-100 dark:hover:text-blue-300"
                          onClick={() => openRouteLogs(r)}
                          title={t('observability.gatewayRouteLogs', 'Open route logs')}
                        >
                          {r.route}
                        </Button>
                        {(r.matchedHost || r.matchedUri || r.ownerService) && (
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                            {(r.matchedHost || r.matchedUri) && (
                              <span className="font-mono">
                                {[r.matchedHost, r.matchedUri].filter(Boolean).join(' ')}
                              </span>
                            )}
                            {r.ownerService && (
                              <span>
                                {t('observability.gatewayOwnerService', 'Logs')}: {r.ownerService}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {/* eslint-disable-next-line i18next/no-literal-string */}
                      <td className="px-4 py-3 text-sm text-right text-slate-700 dark:text-slate-300">{`${r.requestRate.toFixed(2)}/s`}</td>
                      <td className={cn('px-4 py-3 text-sm text-right', r.errorRate > 0.05 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-700 dark:text-slate-300')}>
                        {(r.errorRate * 100).toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-400">
                        {r.latencyP95 > 0 ? `${r.latencyP95.toFixed(0)}ms` : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-2 text-xs text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/40"
                          onClick={() => openRouteLogs(r)}
                        >
                          <Search className="h-3.5 w-3.5" />
                          {t('observability.investigate', 'Investigate')}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
